import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { documentTypesApi } from '@/api/documentTypes';
import { Plus, Pencil, Trash2, Loader2, X } from 'lucide-react';
const FORMAT_OPTIONS = [
    { value: 'url', label: 'URL' },
    { value: 'pdf', label: 'PDF' },
    { value: 'image', label: 'Imagen' },
];
export function DocumentTypesManager() {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        allowedFormats: ['url', 'pdf', 'image'],
        required: false,
        visibleInPortal: true,
    });
    useEffect(() => { load(); }, []);
    async function load() {
        setLoading(true);
        try {
            const data = await documentTypesApi.getAll();
            setTypes(data);
        }
        catch (e) {
            console.error('Failed to load document types:', e);
        }
        finally {
            setLoading(false);
        }
    }
    function openCreate() {
        setEditing(null);
        setFormData({ name: '', allowedFormats: ['url', 'pdf', 'image'], required: false, visibleInPortal: true });
        setShowForm(true);
    }
    function openEdit(t) {
        setEditing(t);
        setFormData({
            name: t.name,
            allowedFormats: t.allowedFormats,
            required: t.required,
            visibleInPortal: t.visibleInPortal,
        });
        setShowForm(true);
    }
    function closeForm() {
        setShowForm(false);
        setEditing(null);
    }
    async function handleSubmit(e) {
        e.preventDefault();
        if (!formData.name?.trim()) {
            alert('El nombre es requerido');
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await documentTypesApi.update(editing.id, formData);
            }
            else {
                await documentTypesApi.create(formData);
            }
            closeForm();
            load();
        }
        catch (err) {
            alert(err instanceof Error ? err.message : 'Error al guardar');
        }
        finally {
            setSaving(false);
        }
    }
    async function handleDelete(t) {
        if (!confirm(`¿Eliminar tipo "${t.name}"?`))
            return;
        try {
            await documentTypesApi.delete(t.id);
            load();
        }
        catch (err) {
            alert(err instanceof Error ? err.message : 'No se puede eliminar: tiene documentos asociados');
        }
    }
    function toggleFormat(format) {
        const current = formData.allowedFormats ?? [];
        const updated = current.includes(format)
            ? current.filter(f => f !== format)
            : [...current, format];
        setFormData(prev => ({ ...prev, allowedFormats: updated }));
    }
    if (loading) {
        return (_jsx("div", { className: "flex justify-center py-8", children: _jsx(Loader2, { className: "w-6 h-6 animate-spin text-[var(--color-text-muted)]" }) }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-sm font-medium text-[var(--color-text)]", children: "Tipos de documento" }), _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "Configura qu\u00E9 documentos pueden cargarse por pasajero" })] }), _jsxs("button", { className: "btn btn-primary btn-sm", onClick: openCreate, children: [_jsx(Plus, { className: "w-4 h-4" }), "Nuevo tipo"] })] }), types.length === 0 && !showForm && (_jsx("div", { className: "text-center py-6 text-sm text-[var(--color-text-muted)]", children: "No hay tipos de documento configurados" })), _jsx("div", { className: "space-y-2", children: types.map(t => (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-[var(--color-text)]", children: t.name }), _jsxs("div", { className: "flex items-center gap-2 mt-1", children: [t.allowedFormats.map(f => (_jsx("span", { className: "text-xs px-2 py-0.5 rounded bg-[var(--color-surface-raised)] text-[var(--color-text-muted)]", children: FORMAT_OPTIONS.find(o => o.value === f)?.label ?? f }, f))), t.required && _jsx("span", { className: "text-xs px-2 py-0.5 rounded bg-[var(--color-error-light)] text-[var(--color-error)]", children: "Obligatorio" }), t.visibleInPortal && _jsx("span", { className: "text-xs px-2 py-0.5 rounded bg-[var(--color-success-light)] text-[var(--color-success)]", children: "Visible portal" })] })] }), _jsx("button", { className: "p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-surface-raised)]", onClick: () => openEdit(t), title: "Editar", children: _jsx(Pencil, { className: "w-4 h-4" }) }), _jsx("button", { className: "p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error)]", onClick: () => handleDelete(t), title: "Eliminar", children: _jsx(Trash2, { className: "w-4 h-4" }) })] }, t.id))) }), showForm && (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50", children: _jsxs("div", { className: "w-full max-w-md bg-white rounded-lg shadow-xl", children: [_jsxs("div", { className: "flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]", children: [_jsx("h3", { className: "text-base font-semibold", children: editing ? 'Editar tipo' : 'Nuevo tipo de documento' }), _jsx("button", { onClick: closeForm, className: "p-1 rounded hover:bg-[var(--color-surface-raised)]", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "p-6 space-y-4", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "input-label", children: "Nombre" }), _jsx("input", { className: "input", value: formData.name ?? '', onChange: e => setFormData(prev => ({ ...prev, name: e.target.value })), placeholder: "Ej: Boleto A\u00E9reo" })] }), _jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "input-label", children: "Formatos permitidos" }), _jsx("div", { className: "flex gap-4", children: FORMAT_OPTIONS.map(f => (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: formData.allowedFormats?.includes(f.value) ?? false, onChange: () => toggleFormat(f.value), className: "w-4 h-4" }), f.label] }, f.value))) })] }), _jsxs("div", { className: "flex gap-4", children: [_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: formData.required ?? false, onChange: e => setFormData(prev => ({ ...prev, required: e.target.checked })), className: "w-4 h-4" }), "Obligatorio"] }), _jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: formData.visibleInPortal ?? true, onChange: e => setFormData(prev => ({ ...prev, visibleInPortal: e.target.checked })), className: "w-4 h-4" }), "Visible en portal del pasajero"] })] }), _jsxs("div", { className: "flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]", children: [_jsx("button", { type: "button", className: "btn btn-secondary btn-md", onClick: closeForm, children: "Cancelar" }), _jsx("button", { type: "submit", className: "btn btn-primary btn-md", disabled: saving, children: saving ? _jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : editing ? 'Guardar' : 'Crear' })] })] })] }) }))] }));
}
//# sourceMappingURL=DocumentTypesManager.js.map